import React, { useState, useEffect } from "react";
import { XMarkIcon, ArrowLongRightIcon } from "@heroicons/react/24/outline";
import { useEventStore } from "../../../store/eventStore";

export default function SelectTrainNEngineModal({
	isOpen,
	onClose,
	eventDataList,
	searchDate,
	onConfirm,
}) {
	const { getEngines, getTrainList, getSeatmapLayouts } = useEventStore();

	// 상태 관리
	const [engines, setEngines] = useState([]);
	const [trainList, setTrainList] = useState([]);
	const [seatmapLayouts, setSeatmapLayouts] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);

	// 선택 상태
	const [selectedEngine, setSelectedEngine] = useState(null);
	const [selectedTrain, setSelectedTrain] = useState(null);
	const [selectedSeatmap, setSelectedSeatmap] = useState(null);

	const resolveTrainTypeCode = (trainCode) => {
		const code = String(trainCode || "").toUpperCase();
		if (code.includes("KTX")) return "KTX";
		if (code.includes("ITX")) return "ITX-SAE";
		if (code.includes("SANTA")) return "SANTA";
		if (code.includes("국악와인") || code.includes("GUGAK")) return "GUGAK_WINE";
		return null;
	};

	const normalizeTrainTypeCode = (code) => {
		const normalized = String(code || "").trim();
		if (!normalized) return "";
		if (normalized.startsWith("BUS_")) return normalized.replace("_", "");
		return normalized;
	};

	const formatSeatmapLabel = (layout) => {
		const typeCode =
			normalizeTrainTypeCode(layout?.train_type_code) ||
			String(layout?.train_type_name || "").trim();
		const name = String(layout?.name || "").trim();
		const variant = layout?.variant ? ` (${layout.variant})` : "";
		if (typeCode && name) return `${typeCode} - ${name}${variant}`;
		if (name) return `${name}${variant}`;
		if (typeCode) return `${typeCode}${variant}`;
		return "좌석배치도";
	};

	useEffect(() => {
		if (!selectedTrain || seatmapLayouts.length === 0) return;
		const typeCode = resolveTrainTypeCode(selectedTrain.train_code);
		if (!typeCode) return;
		const match = seatmapLayouts.find(
			(layout) =>
				String(layout.train_type_code || "").toUpperCase() === typeCode ||
				String(layout.train_type_name || "")
					.toUpperCase()
					.includes(typeCode)
		);
		if (match) {
			setSelectedSeatmap(match);
		}
	}, [selectedTrain, seatmapLayouts]);

	// 데이터 로드
	useEffect(() => {
		if (isOpen && eventDataList && eventDataList.length > 0) {
			loadData();
		}
	}, [isOpen, eventDataList]);

	// 모달이 닫힐 때 선택 상태 초기화
	useEffect(() => {
		if (!isOpen) {
			setSelectedEngine(null);
			setSelectedTrain(null);
			setSelectedSeatmap(null);
		}
	}, [isOpen]);

	const loadData = async () => {
		setLoading(true);
		setError(null);

		try {
			// 엔진 정보 로드
			const enginesResult = await getEngines();
			if (enginesResult.success) {
				setEngines(enginesResult.data || []);
				handleEngineSelect(enginesResult.data[0]);
			} else {
				setError("엔진 정보를 불러오는데 실패했습니다.");
			}

			// 열차 정보 로드 (첫 번째 이벤트의 날짜 사용)
			if (eventDataList && eventDataList.length > 0) {
				const trainResult = await getTrainList(searchDate);
				if (trainResult.success) {
					setTrainList(trainResult.data || []);
				} else {
					setError((prev) =>
						prev
							? `${prev}, 열차 정보를 불러오는데 실패했습니다.`
							: "열차 정보를 불러오는데 실패했습니다."
					);
				}
			}

			// 좌석배치도 레이아웃 로드
			const layoutResult = await getSeatmapLayouts();
			if (layoutResult.success) {
				setSeatmapLayouts(layoutResult.data || []);
				if (layoutResult.data && layoutResult.data.length > 0) {
					setSelectedSeatmap(layoutResult.data[0]);
				}
			}
		} catch (err) {
			setError("데이터를 불러오는 중 오류가 발생했습니다.");
			console.error("데이터 로드 오류:", err);
		} finally {
			setLoading(false);
		}
	};

	// 엔진 선택 핸들러
	const handleEngineSelect = (engine) => {
		setSelectedEngine(engine);
	};

	// 열차 선택 핸들러
	const handleTrainSelect = (train) => {
		setSelectedTrain(train);
	};

	// 확인 버튼 핸들러
	const handleConfirm = () => {
		if (!selectedEngine || !selectedTrain) {
			alert("열차와 엔진을 모두 선택해주세요.");
			return;
		}

		if (typeof onConfirm === "function") {
			onConfirm(selectedTrain, selectedEngine, selectedSeatmap);
		} else {
			onClose();
		}
	};

	if (!isOpen) return null;

	return (
		<div
			className="fixed inset-0 z-50 overflow-y-auto"
			aria-labelledby="modal-title"
			role="dialog"
			aria-modal="true"
		>
			<div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
				{/* 배경 오버레이 */}
				<div
					className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
					aria-hidden="true"
					onClick={onClose}
				></div>

				{/* 모달 중앙 정렬을 위한 트릭 */}
				<span
					className="hidden sm:inline-block sm:align-middle sm:h-screen"
					aria-hidden="true"
				>
					&#8203;
				</span>

				{/* 모달 내용 */}
				<div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full h-screen-1/2">
					{/* 모달 헤더 */}
					<div className="bg-gray-50 px-4 py-3 border-b border-gray-200 sm:px-6 flex justify-between items-center">
						<h3 className="text-lg leading-6 font-medium text-gray-900">
							열차 및 엔진 선택
						</h3>
						<button
							type="button"
							className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
							onClick={onClose}
						>
							<span className="sr-only">닫기</span>
							<XMarkIcon className="h-6 w-6" aria-hidden="true" />
						</button>
					</div>

					{/* 모달 본문 */}
					<div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
						{loading ? (
							<div className="flex justify-center items-center py-10">
								<div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
							</div>
						) : error ? (
							<div className="text-center py-8">
								<p className="text-red-500">{error}</p>
								<button
									onClick={loadData}
									className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
								>
									다시 시도
								</button>
							</div>
						) : (
							<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
								{/* 왼쪽: 열차 정보 */}
								<div className="border rounded-lg p-4">
									<h4 className="text-lg font-medium text-gray-900 mb-4">
										열차 정보
									</h4>
									<div className="space-y-2 max-h-[400px] overflow-y-auto">
										{trainList && trainList.length > 0 ? (
											trainList.map((train, index) => (
												<div
													key={index}
													onClick={() =>
														handleTrainSelect(train)
													}
													className={`p-3 border rounded-md cursor-pointer transition-colors ${
														selectedTrain === train
															? "bg-blue-50 border-blue-500"
															: "hover:bg-gray-50"
													}`}
												>
													<div className="flex items-center justify-between px-6 mt-1">
														<div className="text-sm font-medium bg-blue-100 text-blue-800 px-3 py-1.5 rounded-md">
															열차번호:{" "}
															{train.train_code}
														</div>

														<div className="flex items-center justify-center space-x-2">
															<span className="font-medium">
																{train.dept_stn}
															</span>
															<ArrowLongRightIcon className="h-5 w-5 text-gray-400" />
															<span className="font-medium">
																{train.arrv_stn}
															</span>
														</div>
													</div>
													<div className="text-sm text-gray-500 mt-3">
														<div className="mt-2 flex justify-between px-12">
															<span className="text-blue-600 font-medium">
																미배정:{" "}
																{
																	train.unassigned_tickets
																}
																석
															</span>
															{train.assigned_tickets >
																0 && (
																<span className="text-green-600 font-medium">
																	배정 완료:{" "}
																	{
																		train.assigned_tickets
																	}
																	석
																</span>
															)}
														</div>
													</div>
												</div>
											))
										) : (
											<div className="text-center py-8 text-gray-500">
												열차 정보가 없습니다.
											</div>
										)}
									</div>
								</div>

								{/* 오른쪽: 좌석배정 엔진 */}
								<div className="border rounded-lg p-4">
									<h4 className="text-lg font-medium text-gray-900 mb-4">
										좌석배정 엔진
									</h4>
									<div className="space-y-2 max-h-[500px] overflow-y-auto">
										{engines && engines.length > 0 ? (
											engines.map((engine, index) => (
												<div
													key={index}
													onClick={() =>
														handleEngineSelect(
															engine
														)
													}
													className={`p-3 border rounded-md cursor-pointer transition-colors ${
														selectedEngine ===
														engine
															? "bg-blue-50 border-blue-500"
															: "hover:bg-gray-50"
													}`}
												>
													<div className="font-medium">
														{engine.DISP_NAME}
													</div>
													<div className="text-sm text-gray-500 mt-1">
														<div>
															엔진 버전:{" "}
															{engine.ENGINE_VER}
														</div>
													</div>
												</div>
											))
										) : (
											<div className="text-center py-8 text-gray-500">
												엔진 정보가 없습니다.
											</div>
										)}
									</div>

									<div className="mt-6 border-t pt-4">
										<h4 className="text-lg font-medium text-gray-900 mb-3">
											좌석배치도
										</h4>
										{seatmapLayouts && seatmapLayouts.length > 0 ? (
											<div className="space-y-2">
												<select
													value={selectedSeatmap?.id || ""}
													onChange={(e) => {
														const next = seatmapLayouts.find(
															(item) =>
																String(item.id) ===
																e.target.value
														);
														setSelectedSeatmap(next || null);
													}}
													className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
												>
													{seatmapLayouts.map((layout) => (
														<option key={layout.id} value={layout.id}>
															{formatSeatmapLabel(layout)}
														</option>
													))}
												</select>
												<p className="text-xs text-gray-500">
													선택한 배치도가 좌석 배정 화면에 표시됩니다.
												</p>
											</div>
										) : (
											<div className="text-center py-6 text-gray-500">
												좌석배치도 정보가 없습니다.
											</div>
										)}
									</div>
								</div>
							</div>
						)}
					</div>

					{/* 모달 푸터 */}
					<div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
						<button
							type="button"
							disabled={
								!selectedEngine || !selectedTrain || loading
							}
							className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
							onClick={handleConfirm}
						>
							다음
						</button>
						<button
							type="button"
							className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
							onClick={onClose}
						>
							취소
						</button>
					</div>
				</div>
			</div>
		</div>
	);
}
